import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { Row, Col, Alert, Button } from 'reactstrap';

import moment from 'moment';
import BootstrapTable from 'react-bootstrap-table-next';

import Loader from '../loader/Loader';
import Pagination from '../pagination/Pagination';
import DropdownSelect from '../formFields/DropdownSelect';
import CopyToClipboard from '../copyToClipboard/CopyToClipboard';
import { CREATE_APP_PATH } from '../../utils/constants';

const filterFields = ['name', 'publisherId', 'key', 'platform'];

const columns = [
  {
    dataField: 'name',
    text: 'App Name',
    sort: true,
    formatter: (cellContent, { name }) => {
      return <CopyToClipboard text={name} />;
    }
  },
  {
    dataField: 'publisherId',
    text: 'Publisher ID',
    sort: true,
    formatter: (cellContent, { publisherId }) => {
      return <CopyToClipboard text={publisherId} />;
    }
  },
  {
    dataField: 'key',
    text: 'Key',
    formatter: (cellContent, { key }) => {
      return <CopyToClipboard text={key} />;
    }
  },
  {
    dataField: 'platform',
    text: 'Platform',
    sort: true
  },
  {
    dataField: 'url',
    text: 'URL',
    formatter: (cellContent, { url }) => {
      return url ? (
        <a href={url} target="_blank" className="link">
          {url}
        </a>
      ) : (
        ''
      );
    }
  },
  {
    dataField: 'creationTime',
    text: 'Added',
    sort: true,
    formatter: (cellContent, { creationTime }) => {
      return moment(creationTime).format('YYYY/MM/DD');
    }
  },
  {
    dataField: 'df1',
    isDummyField: true,
    text: 'Analytics',
    formatter: (cellContent, { name }) => {
      const dashboardLink = '/pages/dashboard#!' + name;
      return (
        <Link to={dashboardLink} className="table-link">
          Show Analytics
        </Link>
      );
    }
  }
];

const Table = props => {
  const { data, columns, page, sizePerPage, onTableChange, totalSize } = props;
  const handleSizePerPageChange = value => {
    onTableChange('sizePerPage', {
      ...props,
      sizePerPage: parseInt(value, 10)
    });
  };
  const handlePageChange = page => {
    onTableChange('page', { ...props, page });
  };
  const handleSearch = event => {
    onTableChange('search', {
      ...props,
      searchText: (event.target.value || '').toLowerCase()
    });
  };

  const tableColumns = [
    {
      dataField: '#',
      isDummyField: true,
      text: '#',
      formatter: (cellContent, row, index) => {
        return ++index + (page - 1) * sizePerPage;
      }
    },
    ...columns
  ];

  const pagerOptions = [
    {
      value: 5,
      text: '5'
    },
    {
      value: 10,
      text: '10'
    },
    {
      value: 25,
      text: '25'
    },
    {
      value: 50,
      text: '50'
    },
    {
      value: totalSize,
      text: 'All'
    }
  ];

  return (
    <div>
      <Row>
        <Col md="6" className="pager">
          Show
          <DropdownSelect
            id="pageDropDown"
            onChange={handleSizePerPageChange}
            value={sizePerPage.toString()}
            options={pagerOptions}
          />
          entries
        </Col>
        <Col
          className="table-search"
          md={{ size: 5, offset: 1 }}
          lg={{ size: 4, offset: 2 }}
        >
          <input
            className="table-search-input"
            placeholder="Search..."
            type="text"
            onChange={handleSearch}
          />
        </Col>
      </Row>
      <BootstrapTable
        remote
        keyField="key"
        data={data}
        columns={tableColumns}
        onTableChange={onTableChange}
      />
      <Pagination
        totalItems={totalSize}
        pageSize={sizePerPage}
        onChangePage={handlePageChange}
        currentPage={page}
      />
    </div>
  );
};

export default class AppsList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      page: 1,
      data: null,
      sizePerPage: 10
    };
  }

  componentDidMount() {
    if (this.props.loading) {
      this.props.fetchAllApps();
    }
  }

  handleTableChange = (
    type,
    { page, sizePerPage, searchText, sortField, sortOrder }
  ) => {
    sizePerPage = sizePerPage || this.state.sizePerPage;
    page = page || this.state.page;
    const { list } = this.props;
    let allItems = this.state.allItems || list;
    let currentIndex = (page - 1) * sizePerPage;

    if (type === 'search') {
      allItems = list.filter(row => {
        let valid = false;
        for (const dataField of filterFields) {
          if (!row[dataField]) continue;
          valid =
            row[dataField]
              .toString()
              .toLowerCase()
              .indexOf(searchText) > -1;
          if (valid) break;
        }
        return valid;
      });
    } else if (type === 'sort') {
      allItems = allItems.sort((a, b) => {
        if (a[sortField] > b[sortField]) {
          return sortOrder === 'asc' ? 1 : -1;
        } else if (b[sortField] > a[sortField]) {
          return sortOrder === 'asc' ? -1 : 1;
        }
        return 0;
      });
    }

    const totalSize = allItems.length;

    if (totalSize === sizePerPage) {
      page = 1;
      currentIndex = 0;
    } else if (totalSize <= currentIndex) {
      page = parseInt(allItems.length / sizePerPage, 10) || 1;
      currentIndex = (page - 1) * sizePerPage;
    }

    this.setState(() => ({
      totalSize,
      page,
      allItems,
      data: allItems.slice(currentIndex, currentIndex + sizePerPage),
      sizePerPage
    }));
  };

  render() {
    const { list, loading, goToCreateAppPage } = this.props;
    let { page, sizePerPage, data, totalSize } = this.state;
    data = data || (list ? list.slice(0, sizePerPage) : []);
    totalSize = isNaN(totalSize) ? (list ? list.length : 0) : totalSize;

    return (
      <Loader loaded={loading}>
        {list.length === 0 ? (
          <Alert color="warning">
            You do not have any apps yet.{' '}
            <Link to={CREATE_APP_PATH}> Add your first one</Link>
          </Alert>
        ) : (
          <div className="content-container">
            <Row>
              <Col sm="7" lg="4" className="page-title">
                Application list
              </Col>
              <Col sm={{ size: 4, offset: 1 }} lg={{ size: 3, offset: 5 }}>
                <Button
                  color="primary"
                  className="add-app-button"
                  size="sm"
                  onClick={goToCreateAppPage}
                >
                  Add App
                </Button>
              </Col>
            </Row>
            <Table
              data={data}
              columns={columns}
              page={page}
              sizePerPage={sizePerPage}
              totalSize={totalSize}
              onTableChange={this.handleTableChange}
            />
          </div>
        )}
      </Loader>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/apps/AppsList.js